import { z } from 'zod'
import { listQuerySchema } from './lists.js'

/** Genere di un invitato: serve solo alle sfide a parità di genere. */
export const MIMI_GENDERS = ['M', 'F'] as const
export type MimiGender = (typeof MIMI_GENDERS)[number]

/**
 * Cosa succede dopo lo spicchio: niente (`plain`), ruota dello sfidante (`challenger`),
 * sfidante dello stesso genere (`sameGender`) o lancio del dado (`dice`).
 */
export const MIMI_TASK_KINDS = ['plain', 'challenger', 'sameGender', 'dice'] as const
export type MimiTaskKind = (typeof MIMI_TASK_KINDS)[number]

const name = z.string().trim().min(1).max(60)
const id = z.string().min(1).max(64)

/** POST/PUT /api/mimi/participants */
export const MimiParticipantInputSchema = z.object({
  name,
  gender: z.enum(MIMI_GENDERS).nullable(),
})
export type MimiParticipantInput = z.infer<typeof MimiParticipantInputSchema>
export type MimiParticipant = MimiParticipantInput & { id: string; createdAt: string }

export const MimiParticipantListQuerySchema = listQuerySchema(['createdAt', 'name'], {
  gender: z.enum(MIMI_GENDERS).optional(),
})
export type MimiParticipantListQuery = z.infer<typeof MimiParticipantListQuerySchema>

/** POST/PUT /api/mimi/tasks — se la descrizione contiene «rigira», dopo lo spicchio compare il pulsante Rigira. */
export const MimiTaskInputSchema = z.object({
  emoji: z.string().trim().min(1).max(16),
  title: z.string().trim().min(1).max(60),
  description: z.string().trim().min(1).max(500),
  kind: z.enum(MIMI_TASK_KINDS),
})
export type MimiTaskInput = z.infer<typeof MimiTaskInputSchema>
export type MimiTask = MimiTaskInput & { id: string; createdAt: string }

export const MimiTaskListQuerySchema = listQuerySchema(['createdAt', 'title'], {
  kind: z.enum(MIMI_TASK_KINDS).optional(),
})
export type MimiTaskListQuery = z.infer<typeof MimiTaskListQuerySchema>

/** POST/PUT /api/mimi/rigs — azione nascosta: quando esce l'invitato, la ruota finisce sulla sfida (e sullo sfidante). */
export const MimiRigInputSchema = z.object({
  participantId: id,
  taskId: id,
  challengerId: id.nullable(),
})
export type MimiRigInput = z.infer<typeof MimiRigInputSchema>
export type MimiRig = MimiRigInput & {
  id: string
  createdAt: string
  participantName: string
  taskTitle: string
  challengerName: string | null
}

export const MimiRigListQuerySchema = listQuerySchema(['participant', 'createdAt'], {})
export type MimiRigListQuery = z.infer<typeof MimiRigListQuerySchema>

/**
 * Ultima animazione della regia, che la pagina pubblica riproduce: `seq` cresce a ogni evento.
 * `offset` (0–1 dentro lo spicchio), `turns` e `duration` rendono il giro identico sui due schermi.
 */
const spinShape = { offset: z.number().min(0).max(1), turns: z.number().int().min(0).max(20), duration: z.number().min(0).max(20000) }
export const MimiEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('draw'), seq: z.number().int(), name, emergency: z.boolean() }),
  z.object({ type: z.literal('spin'), seq: z.number().int(), taskId: id, ...spinShape }),
  z.object({ type: z.literal('challenger'), seq: z.number().int(), cands: z.array(name).min(1).max(500), index: z.number().int().min(0), ...spinShape }),
  z.object({ type: z.literal('dice'), seq: z.number().int(), value: z.number().int().min(1).max(6) }),
])
export type MimiEvent = z.infer<typeof MimiEventSchema>

/**
 * Cronometro «Prossima estrazione», condiviso tra regia e pagina pubblica. In corsa ha `endsAt` (ms, orologio del
 * server: ognuno lo converte con `serverNow`); in pausa `endsAt` è null e vale `remain` (secondi).
 */
export const MimiTimerSchema = z.object({
  dur: z.number().int().min(1).max(120),
  endsAt: z.number().int().positive().nullable(),
  remain: z.number().int().min(0).max(7200),
  ringing: z.boolean(),
})
export type MimiTimer = z.infer<typeof MimiTimerSchema>
export const DEFAULT_MIMI_TIMER: MimiTimer = { dur: 7, endsAt: null, remain: 7 * 60, ringing: false }

/** Stato della serata: PUT /api/mimi/state dalla regia; `nextUp`/`nextFx` (atterraggio d'emergenza) restano segreti. */
export const MimiStateSchema = z.object({
  drawn: z.array(name).max(500),
  absent: z.array(name).max(500),
  history: z.array(z.object({ name, text: z.string().max(500), time: z.string().max(10) })).max(60),
  nextUp: name.nullable(),
  nextFx: z.boolean(),
  turn: z
    .object({
      phase: z.string().max(30),
      name,
      taskId: id.nullable(),
      challenger: name.nullable(),
      dice: z.number().int().min(1).max(6).nullable(),
      extraShots: z.number().int().min(0).max(1000),
      respins: z.number().int().min(0).max(1000),
      /** Candidati della ruota dello sfidante, finché lo sfidante non è scelto. */
      cands: z.array(name).max(500).nullable(),
    })
    .nullable(),
  event: MimiEventSchema.nullable(),
  timer: MimiTimerSchema.default(DEFAULT_MIMI_TIMER),
})
export type MimiState = z.infer<typeof MimiStateSchema>

export const EMPTY_MIMI_STATE: MimiState = {
  drawn: [],
  absent: [],
  history: [],
  nextUp: null,
  nextFx: true,
  turn: null,
  event: null,
  timer: DEFAULT_MIMI_TIMER,
}

/** GET /api/mimi/game (regia): tutto il necessario per giocare, azioni nascoste comprese. */
export type MimiGame = {
  participants: Pick<MimiParticipant, 'id' | 'name' | 'gender'>[]
  tasks: Pick<MimiTask, 'id' | 'emoji' | 'title' | 'description' | 'kind'>[]
  rigs: Pick<MimiRig, 'participantName' | 'taskId' | 'challengerName'>[]
  state: MimiState
  /** Date.now() del server: allinea il cronometro tra dispositivi. */
  serverNow: number
}

/** GET /api/mimi/live (pubblico): nomi degli invitati, spicchi e stato, senza generi né segreti. */
export type MimiLive = {
  participants: string[]
  tasks: Pick<MimiTask, 'id' | 'emoji' | 'title' | 'description'>[]
  state: Omit<MimiState, 'nextUp' | 'nextFx'>
  serverNow: number
}
